import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { FunctionComponent, useState } from 'react';

import { UserCard } from '../../../../../components/UserCard/UserCard';
import { LikeAdDialog } from '../LikeAdDialog';
import { WeddingAdDialog } from '../WeddingAdDialog';
import './TinderSwipe.css';
import { useTinderSwipe } from './useTinderSwipe';

interface TinderSwipeProps {
  eventId: string;
}

export const TinderSwipe: FunctionComponent<TinderSwipeProps> = ({
  eventId,
}) => {
  const [, setLikePressCount] = useState(0);
  const [isLikeAdOpen, setIsLikeAdOpen] = useState(false);
  const [isWeddingAdOpen, setIsWeddingAdOpen] = useState(false);
  const {
    canSwipe,
    cards,
    swiped,
    swipe,
    childRefs,
    currentImageIndex,
    onNextImagePress,
    onPreviousImagePress,
    numberOfImages,
    hasSeenAllProfiles,
    currentIndex,
    isUsersLoading,
    remainingSuperLikes,
  } = useTinderSwipe(eventId);

  const onLikePress = () => {
    if (!canSwipe) {
      return;
    }

    let shouldOpenAd = false;
    setLikePressCount((prev) => {
      const nextLikePressCount = prev + 1;
      shouldOpenAd = nextLikePressCount % 10 === 0;

      return nextLikePressCount;
    });

    if (shouldOpenAd) {
      setIsLikeAdOpen(true);
      return;
    }

    void swipe('right');
  };

  const onCloseLikeAd = () => {
    setIsLikeAdOpen(false);
  };

  const onWatchAd = () => {
    setIsLikeAdOpen(false);
    setIsWeddingAdOpen(true);
  };

  if (isUsersLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Box
        sx={{
          maxWidth: '100%',
          height: 500,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            borderRadius: 4,
            height: '450px',
            width: '300px',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h2">Fin</Typography>
        </Box>

        {[...cards].reverse().map((card, index, cards) => (
          <UserCard
            key={card.id}
            cardRef={childRefs[index]}
            onSwipe={(dir) => swiped(dir)}
            user={card}
            currentImageIndex={currentImageIndex}
            currentIndex={currentIndex}
            index={cards.length - index - 1}
            canSwipeUp={remainingSuperLikes > 0}
          />
        ))}
      </Box>

      {hasSeenAllProfiles ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
          }}
        >
          <Typography>Vous avez vu tous le monde !</Typography>
        </Box>
      ) : numberOfImages > 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            p: 2,
          }}
        >
          <IconButton onClick={onPreviousImagePress}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography>
            Image {currentImageIndex + 1}/{numberOfImages}
          </Typography>
          <IconButton onClick={onNextImagePress}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
          }}
        >
          <Typography>Aucune image</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, p: 2 }}>
        <Button
          variant="outlined"
          disabled={!canSwipe}
          onClick={() => swipe('left')}
          startIcon={<CloseIcon />}
        >
          Dislike
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSwipe || remainingSuperLikes <= 0}
          onClick={() => swipe('up')}
          startIcon={<StarIcon />}
        >
          Super Like ({remainingSuperLikes})
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!canSwipe}
          onClick={onLikePress}
          startIcon={<LocalFireDepartmentIcon />}
        >
          Like
        </Button>
      </Box>
      <LikeAdDialog
        open={isLikeAdOpen}
        onClose={onCloseLikeAd}
        onWatch={onWatchAd}
      />
      <WeddingAdDialog
        open={isWeddingAdOpen}
        onClose={() => setIsWeddingAdOpen(false)}
      />
    </Box>
  );
};
